import React, { useEffect, useMemo, useState } from 'react';

import * as Yup from 'yup';
import classNames from 'classnames';
import cardValidator from 'card-validator';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';

import Button from 'components/CustomButtons/Button';

import TextField from '@material-ui/core/TextField';
import FormLabel from '@material-ui/core/FormLabel';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { makeStyles } from '@material-ui/core/styles';
import styles, { AntSwitch as Switch } from 'assets/jss/material-dashboard-pro-react/components/paymentMethodsStyle';

import PaymentProcessing from './../common/PaymentProcessing';

import { setBillValue } from 'redux/modules/bills/actions';
import { upIdSelector, userData } from 'redux/modules/session/selectors';
import { setPaymentType } from 'redux/modules/paymentType/actions';
import { getPaymentMethodLabels } from 'redux/modules/dynamicLabels/selectors';

import PaymentService from 'services/payments/payment';

import { finalize } from 'rxjs/operators';
import { getLanguage } from 'utils/language/language';

import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import validationProps from '../../helper/validation';
import { PaymentRedirection } from '../../helper/payment';

import AddCard from './AddCard';
import CardList from './CardList';
import { MY_BILLS_PAY } from 'components/PaymentMethod/constant';
import { usePaymentMethod } from 'components/PaymentMethod/context';

import { CardPaymentDetails, CardPaymentToken, PayWith, PaymentMethodProps, PaymentStatusType } from '../../type';

import {
	CREDIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_DONATION_AMOUNT,
	CURRENCY,
	DEBIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_TOPUP_AMOUNT,
	ERROR_MESSAGES_GA,
	EVENTS_FAILURE,
	EVENTS_SUCCESS,
	PAYER_AUTHENTICATION_REQUIRED,
	PAYMENT_METHOD,
	PAYMENT_METHODS,
	TRANSACTION_REJECTED,
} from 'views/Bills/constants';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { isBOMOn } from 'utils/killswitch';
import { loadQualtrics } from 'utils/qualtrics';

// @ts-ignore
const useStyles = makeStyles(styles);

type CardPaymentProps = PaymentMethodProps;

const CardPayment = ({ bills, onPaymentResult, paymentPreference, referrer = '' }: CardPaymentProps) => {
	const classes = useStyles();
	const dispatch = useDispatch();

	const {
		tokens: { Cards },
		onAddCard,
	} = usePaymentMethod();

	const accId = useSelector(upIdSelector);
	const userDetails = useSelector(userData);
	const paymentMethodLabels = useSelector(getPaymentMethodLabels);

	// kill switch to check if Bank Muscat are to be shown
	const remoteConfig = useSelector(remoteConfigSelector);
	const isBankMuscatEnabled = isBOMOn(remoteConfig);

	const [addCardDialogOpen, setAddCardDialogOpen] = useState(false);

	const onAddingCard = (card: CardPaymentToken): void => {
		onAddCard(card);
		setAddCardDialogOpen(false);
	};

	const onMakePayment = (values: CardPaymentDetails, actions: FormikHelpers<CardPaymentDetails>) => {
		actions.setSubmitting(true);
		const { month, year } = cardValidator.expirationDate(Cards.defaultToken?.cardExpiry ?? '');

		setDataLayer(MY_BILLS_PAY, getLanguage(), PAYMENT_METHOD, accId, null, {
			stepNo: 2,
			paymentMethod: PAYMENT_METHODS.CARD,
		});

		const payload: types.payment.MakePayment = {
			type: 'PAY',
			quantity: 1,
			createDefaultPayment: values.defaultOption,
			createPaymentToken: Cards.defaultToken?.savedOption ?? false,
			items: bills as types.payment.paymentItem[],
			billingAddress: {
				firstName: userDetails.userName,
				lastName: userDetails.userName,
				country: 'OM',
				email: values.email,
				city: '',
				street1: '',
			},
			...(!Cards.defaultToken?.isUnSaved
				? { paymentToken: Cards.defaultToken?.id }
				: {
						card: {
							cvv: Cards.defaultToken?.cardCVV ?? '',
							expiration: { month: month?.toString() ?? '', year: year?.toString() ?? '' },
							number: Cards.defaultToken?.cardNumber,
							type: cardValidator
								.number(Cards.defaultToken?.cardNumber)
								.card?.type.toLocaleUpperCase() as types.payment.cardTypes,
						},
				  }),
			totalPrice: bills.reduce((acc, curr) => {
				return acc + parseFloat(curr?.unitPrice?.toString());
			}, 0),
		};

		dispatch(setBillValue({ billValue: payload.totalPrice }));

		PaymentService.makePayment(payload)
			.pipe(finalize(() => actions.setSubmitting(false)))
			.subscribe(
				() => {
					setDataLayer(EVENTS_SUCCESS[referrer], getLanguage(), null, accId, null, {
						stepNo: 2,
						paymentMethod: PAYMENT_METHODS.CARD,
						referenceID: '', // TODO(BM): add ref. no from api response
						value: payload.totalPrice,
						currency: CURRENCY.OMR,
						items: payload.items.map((paymentData) => {
							return {
								billPeriod: paymentData.month,
								billAmount: paymentData.unitPrice,
							};
						}),
					});

					actions.setSubmitting(false);
					if (onPaymentResult) onPaymentResult(PaymentStatusType.SUCCESS);
					setTimeout(() => {
						loadQualtrics({
							activity_type: `Bill Payment with Card  - Success`,
						});
					}, 2000);
				},
				(error) => {
					let errorMsg = '';
					switch (error?.response?.code) {
						case PAYER_AUTHENTICATION_REQUIRED:
							actions.setSubmitting(false);
							dispatch(setPaymentType({ action: 'OTHER' }));
							errorMsg = ERROR_MESSAGES_GA[PAYER_AUTHENTICATION_REQUIRED];
							PaymentRedirection(error?.response?.url);
							break;

						case CREDIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_DONATION_AMOUNT:
							errorMsg = ERROR_MESSAGES_GA[CREDIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_DONATION_AMOUNT];
							if (onPaymentResult) onPaymentResult(PaymentStatusType.CREDIT_EXCEEDS_MAX);
							break;

						case DEBIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_TOPUP_AMOUNT:
							errorMsg = ERROR_MESSAGES_GA[DEBIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_TOPUP_AMOUNT];
							if (onPaymentResult) onPaymentResult(PaymentStatusType.DEBIT_EXCEEDS_MAX);
							break;

						case TRANSACTION_REJECTED:
							errorMsg = ERROR_MESSAGES_GA[TRANSACTION_REJECTED];
							if (onPaymentResult) onPaymentResult(PaymentStatusType.REJECTED);
							break;

						default:
							if (onPaymentResult) onPaymentResult(PaymentStatusType.ERROR);
							break;
					}
					if (error?.response?.code != PAYER_AUTHENTICATION_REQUIRED) {
						setTimeout(() => {
							loadQualtrics({
								activity_type: `Bill Payment with Card  - Error(${error?.response?.code})`,
							});
						}, 2000);
					}
					setDataLayer(EVENTS_FAILURE[referrer], getLanguage(), null, accId, null, {
						stepNo: 2,
						paymentMethod: PAYMENT_METHODS.CARD,
						errorMsg: errorMsg,
						errSrc: '', // TODO(BM): add ERROR SOURCE from api response bankMuscat or Omantel
						errTktID: '', // TODO(BM): add TICKET ID from api response bankMuscat
					});
				}
			);
	};

	const FormSchema = Yup.object().shape({
		email: Yup.string()
			.required(paymentMethodLabels?.emailRequired)
			.max(80, paymentMethodLabels?.emailInValid)
			.matches(/^[^\s][A-Za-z0-9._%'-+]+@[A-Za-z0-9.-]+\.[a-zA-Z]{2,62}$/, paymentMethodLabels?.emailInvalid),
		savedOption: Yup.boolean(),
		defaultOption: Yup.boolean(),
	});

	return (
		<Formik
			onSubmit={onMakePayment}
			validationSchema={FormSchema}
			initialValues={{
				email: '',
				defaultOption: paymentPreference === PayWith.CARD,
			}}
		>
			{(props: FormikProps<CardPaymentDetails>) => {
				return (
					<>
						{props.isSubmitting && <PaymentProcessing visible />}
						<Form
							style={{ width: '100%' }}
							onSubmit={props.handleSubmit}
							className={classes.paymentMethodWrapper}
						>
							<CardList />
							<div className={classes.billDetailsContainer}>
								<Button
									fullWidth
									color="secondary-light"
									onClick={() => setAddCardDialogOpen(true)}
									className={classNames(classes.addCardButton)}
								>
									{paymentMethodLabels?.add_card}
								</Button>
							</div>

							<div className={classes.billDetailsContainer}>
								<FormLabel className={classes.cardDetailTitle} htmlFor="email">
									{paymentMethodLabels?.billingEmail}
								</FormLabel>
								<TextField
									id="email"
									className={classes.billingEmailInput}
									style={{ margin: 8 }}
									fullWidth
									margin="normal"
									onChange={props.handleChange}
									onBlur={props.handleBlur}
									InputLabelProps={{ shrink: true }}
									{...validationProps('email', props.errors, props.touched)}
								/>
							</div>
							{isBankMuscatEnabled && (
								<div className={classes.billDetailsContainer}>
									<div className={classNames(classes.accordionElement)}>
										<FormControlLabel
											className={classes.switchLabel}
											control={
												<Switch
													id="default_card_method"
													name="defaultOption"
													checked={props.values.defaultOption}
													onChange={props.handleChange}
												/>
											}
											label={paymentMethodLabels?.defaultPaymentMethod}
										/>
									</div>
								</div>
							)}
							<div className={classes.billDetailsContainer}>
								<Button
									fullWidth
									type="submit"
									color="primary-light"
									disabled={props.isSubmitting || !Cards.defaultToken}
								>
									{paymentMethodLabels?.makePayment}
								</Button>
							</div>
						</Form>
						<AddCard
							onAddCard={onAddingCard}
							isOpen={addCardDialogOpen}
							onClose={() => setAddCardDialogOpen(false)}
						/>
					</>
				);
			}}
		</Formik>
	);
};

export default CardPayment;
